import { useState, useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { selectCurrentProject } from '../../../../store/slices/user';
import { openEventSource } from "../../../../api/event-source";

const STAGE_NAMES = {
  started: 'Starting...',
  building_context: 'Preparing context...',
  sending_to_llm: 'Sending to AI...',
  waiting_for_llm: 'AI is thinking...',
  received_from_llm: 'Received response...',
  parsing_response: 'Processing response...',
  storing_response: 'Saving results...',
  completed: 'Completed!',
  error: 'Error occurred'
};

/**
 * Hook to track progress of AI requests using Server-Sent Events
 * @param {string|null} requestId - The request ID to track
 * @param {string} baseUrl - Base URL for API (default: axios baseURL)
 * @returns {{progress: Object|null, error: string|null, stageName: string}}
 */
export function useProgressTracking(requestId, baseUrl = null) {
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const updateQueueRef = useRef([]);
  const isProcessingQueueRef = useRef(false);
  const currentProject = useSelector(selectCurrentProject);

  // Process queued updates with animation delay
  const processUpdateQueue = useCallback(() => {
    if (isProcessingQueueRef.current || updateQueueRef.current.length === 0) {
      return;
    }

    isProcessingQueueRef.current = true;

    const processNext = () => {
      if (updateQueueRef.current.length === 0) {
        isProcessingQueueRef.current = false;
        return;
      }

      const nextUpdate = updateQueueRef.current.shift();
      setProgress(nextUpdate);

      // Delay before next update (shorter for historical, longer for real-time)
      const delay = nextUpdate.isHistorical ? 300 : 100;
      setTimeout(processNext, delay);
    };

    processNext();
  }, []);

  useEffect(() => {
    if (!requestId) {
      setProgress(null);
      setError(null);
      setIsConnected(false);
      updateQueueRef.current = [];
      isProcessingQueueRef.current = false;
      return;
    }

    // Get base URL from axios defaults if not provided
    // LF360-1970: the session cookie carries this stream now, so an
    // administrator's JWT no longer travels in a URL. `openEventSource`
    // still appends a token left over from before the cookie release.
    const eventSource = openEventSource(
      `/api/scenario-editor-ai/progress/${requestId}/stream`,
      { projectId: currentProject?.id },
      baseUrl,
    );
    eventSourceRef.current = eventSource;

    // Log connection state changes
    eventSource.onopen = () => {
      setIsConnected(true);
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Queue the update for animated display
        updateQueueRef.current.push(data);
        processUpdateQueue();

        setError(null);
      } catch (err) {
        console.error('[ProgressTracking] Failed to parse progress data:', err, 'Raw data:', event.data);
        setError('Failed to parse progress data');
      }
    };

    eventSource.addEventListener('done', (event) => {
      try {
        const data = JSON.parse(event.data);

        // Queue the final update
        updateQueueRef.current.push(data);
        processUpdateQueue();

        eventSource.close();

        // Clear progress after all queued updates are shown plus delay
        const queueLength = updateQueueRef.current.length;
        const totalDelay = (queueLength * 300) + 2000;
        setTimeout(() => {
          setProgress(null);
        }, totalDelay);
      } catch (err) {
        console.error('[ProgressTracking] Failed to parse done event:', err);
      }
    });

    eventSource.addEventListener('error', (event) => {
      try {
        const data = JSON.parse(event.data);
        console.error('[ProgressTracking] SSE error event:', data);
        setError(data.error || 'Connection error');
      } catch (err) {
        // Error event might not have data
        console.error('[ProgressTracking] SSE connection error');
        setError('Connection error');
      }
      eventSource.close();
    });

    eventSource.onerror = () => {
      // Check if we have progress before setting error
      // Using ref to avoid dependency on progress state
      if (updateQueueRef.current.length === 0) {
        setError('Failed to connect to progress stream');
      }
      eventSource.close();
    };

    // Cleanup on unmount or requestId change
    return () => {
      if (eventSource.readyState !== EventSource.CLOSED) {
        eventSource.close();
      }
      // Clear animation queue
      updateQueueRef.current = [];
      isProcessingQueueRef.current = false;
    };
  }, [requestId, baseUrl, currentProject, processUpdateQueue]);

  const stageName = progress?.stage
    ? STAGE_NAMES[progress.stage] || progress.stage
    : 'Initializing...';

  return {
    progress,
    error,
    stageName,
    stage: progress?.stage || null,
    elapsed: progress?.elapsed || 0,
    details: progress?.details || {},
    isConnected
  };
}

/**
 * Fallback hook using polling (for browsers that don't support SSE)
 * @param {string|null} requestId - The request ID to track
 * @param {string} baseUrl - Base URL for API (default: axios baseURL)
 * @param {number} interval - Polling interval in ms (default: 1000)
 * @returns {{progress: Object|null, error: string|null, stageName: string}}
 */
export function useProgressPolling(requestId, baseUrl = null, interval = 1000) {
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!requestId) {
      setProgress(null);
      setError(null);
      return;
    }

    // Get base URL from axios defaults if not provided
    const apiBaseUrl = baseUrl || axios.defaults.baseURL || '';

    const fetchProgress = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/scenario-editor-ai/progress/${requestId}`);
        const result = await response.json();

        if (result.success) {
          setProgress(result.data);
          setError(null);

          // Stop polling if completed or error
          if (result.data.stage === 'completed' || result.data.stage === 'error') {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
          }
        } else {
          setError(result.error || 'Failed to fetch progress');
        }
      } catch (err) {
        console.error('[ProgressPolling] Failed to fetch progress:', err);
        setError('Failed to fetch progress');
      }
    };

    // Fetch immediately
    fetchProgress();

    // Then poll at interval
    intervalRef.current = setInterval(fetchProgress, interval);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [requestId, baseUrl, interval]);

  const stageName = progress?.stageName || progress?.stage
    ? STAGE_NAMES[progress.stage] || progress.stage
    : 'Initializing...';

  return {
    progress,
    error,
    stageName,
    stage: progress?.stage || null,
    elapsed: progress?.elapsed || 0,
    details: progress?.details || {}
  };
}

export default useProgressTracking;
